import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { DeepReactive } from '@jack-henry/frontend-utils/lit';
import { Wire, WiresService, WireTemplate } from '@treasury/domain/wires';
import { TmBaseComponent } from '@treasury/presentation';
import { circleCheckmarkIcon, circleWarningIcon } from '@treasury/presentation/assets/icons';
import { debounce } from '@treasury/utils';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

enum IconState {
    Null,
    Valid,
    Invalid,
}

export const tagName = 'wire-create-template';
@customElement(tagName)
export class WireCreateTemplate extends TmBaseComponent {
    @property({ type: Object })
    public wire: Wire = new Wire();

    @InjectProperty()
    declare private wireService: WiresService;

    @DeepReactive()
    private wireTemplate: WireTemplate = new WireTemplate();

    @state()
    private saveTemplate = false;

    @state()
    private isValidTemplateName = true;

    @state()
    private iconState = IconState.Null;

    private debouncedTemplateName = debounce(() => this.validateTemplateName(), 500);

    private get iconConfig() {
        if (this.wireTemplate.name.length < 3) {
            this.iconState = IconState.Null;
        }
        switch (this.iconState) {
            case IconState.Valid:
                return { icon: circleCheckmarkIcon, color: `var(--content-positive)` };
            case IconState.Invalid:
                return { icon: circleWarningIcon, color: `var(--content-negative)` };
            case IconState.Null:
            default:
                // eslint-disable-next-line no-restricted-syntax
                return { icon: null, color: `` };
        }
    }

    protected render() {
        return html`<div class="px-6">
            <tm-checkbox
                label="Save as template"
                @checked-changed=${(e: CustomEvent) => {
                    this.saveTemplate = e.detail.value;
                    this.updateWireTemplate();
                }}
            ></tm-checkbox>
            ${this.saveTemplate
                ? html` <div class="mt-2 text-xs text-header">Template Name</div>
                      <tm-text-field
                          .initial=${this.wireTemplate.name}
                          .invalid=${!this.isValidTemplateName}
                          .helperText=${!this.isValidTemplateName
                              ? 'Wire template name must be unique.'
                              : ''}
                          @value-changed=${(e: CustomEvent) => {
                              if (e.detail.value) {
                                  this.wireTemplate.name = e.detail.value;
                                  this.debouncedTemplateName();
                              }
                          }}
                      >
                          <tm-icon
                              slot="suffix"
                              .icon=${this.iconConfig.icon}
                              .color=${this.iconConfig.color}
                          ></tm-icon>
                      </tm-text-field>`
                : nothing}
        </div>`;
    }

    private updateWireTemplate() {
        this.wireTemplate.toCreateDto(this.wire);
        this.dispatchEvent(
            new CustomEvent('update', {
                detail: {
                    saveTemplate: this.saveTemplate,
                    wireTemplate: this.wireTemplate,
                    isValidTemplateName: this.isValidTemplateName,
                },
            })
        );
    }

    private async validateTemplateName() {
        const response = (await this.wireService.validateTemplateName(
            this.wireTemplate.name
        )) as unknown as { isValid: boolean };
        this.isValidTemplateName = response.isValid;
        this.iconState = this.isValidTemplateName ? IconState.Valid : IconState.Invalid;
        this.updateWireTemplate();
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: WireCreateTemplate;
    }
}
